import React, { useEffect, useState } from "react";
import api from "../services/api";
import DateRangeFilter from "../components/DateRangeFilter";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const COLORS = ["#10b981", "#ef4444"]; // Green for Credit, Red for Debit

const AllWalletTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState({});
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 25);
      if (typeFilter) params.append("type", typeFilter);
      if (dateRange.startDate) params.append("startDate", dateRange.startDate);
      if (dateRange.endDate) params.append("endDate", dateRange.endDate);

      const res = await api.get(`/wallet/transactions?${params.toString()}`);

      if (res.data.success) {
        setTransactions(res.data.transactions);
        setTotalPages(res.data.totalPages);
        setTotal(res.data.total);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchChart = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append("startDate", dateRange.startDate);
      if (dateRange.endDate) params.append("endDate", dateRange.endDate);

      const res = await api.get(`/wallet/chart?${params.toString()}`);
      if (res.data.success) {
        setChartData(res.data.chartData);
        setSummary(res.data.summary);
      }
    } catch (err) {
      // chart failure shouldn't block the page
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchChart();
    // eslint-disable-next-line
  }, [typeFilter, page, dateRange]);

  if (loading && transactions.length === 0) {
    return <div style={styles.loading}>Loading Transactions...</div>;
  }

  // Calculate Pie Chart Data
  const pieData = summary
    ? [
        { name: "Earnings", value: summary.totalCredit || 0 },
        { name: "Withdrawn", value: summary.totalDebit || 0 },
      ]
    : [];

  // Calculate Cumulative Net Balance Trend for Line Chart
  let runningBalance = 0;
  const trendData = chartData.map((d) => {
    const net = (d.credit || 0) - (d.debit || 0);
    runningBalance += net;
    return {
      date: d.date,
      balance: runningBalance,
      credit: d.credit || 0,
      debit: d.debit || 0
    };
  });

  return (
    <div style={styles.page}>
      <style>
        {`
          .transactions-container {
            padding: 30px;
          }
          .charts-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 20px;
            margin-bottom: 24px;
          }
          .full-width-chart {
            grid-column: 1 / -1;
          }
          .table-header-desktop {
            display: flex;
          }
          .transaction-row {
            display: flex;
            flex-direction: row;
            align-items: center;
          }
          .mobile-label {
            display: none;
            font-size: 11px;
            font-weight: 700;
            color: #94a3b8;
            text-transform: uppercase;
            margin-bottom: 2px;
          }

          @media (max-width: 768px) {
            .transactions-container {
              padding: 16px !important;
            }
            .header-flex {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .table-header-desktop {
              display: none !important;
            }
            .transaction-row {
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 12px;
              padding: 16px !important;
            }
            .mobile-label {
              display: block !important;
            }
            .tx-cell {
              width: 100% !important;
              flex: unset !important;
            }
            .charts-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      <div className="transactions-container" style={styles.innerWrapper}>
        <div className="header-flex" style={styles.header}>
          <div>
            <h2 style={styles.title}>All Wallet Transactions</h2>
            <p style={styles.subtitle}>{total} total transactions across all drivers</p>
          </div>

          <select
            style={styles.select}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Types</option>
            <option value="CREDIT">Credit</option>
            <option value="DEBIT">Debit</option>
          </select>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <DateRangeFilter
          onChange={(range) => {
            setDateRange(range);
            setPage(1);
          }}
        />

        {summary && (
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>Total Earnings (Range)</span>
              <span style={styles.statValue}>₹{summary.totalCredit}</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>Total Withdrawn (Range)</span>
              <span style={styles.statValue}>₹{summary.totalDebit}</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>Total Commission (Range)</span>
              <span style={styles.statValue}>₹{summary.totalCommission}</span>
            </div>
          </div>
        )}

        {/* Multi-Chart Analytics Dashboard */}
        {chartData.length > 0 && (
          <div className="charts-grid">
            {/* 1. Bar Chart: Daily Earnings vs Withdrawals */}
            <div style={styles.chartCard}>
              <div style={styles.chartTitle}>Daily Breakdown (Earnings vs Withdrawals)</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" tickLine={false} />
                  <YAxis fontSize={11} stroke="#94a3b8" tickLine={false} axisLine={false} />
                  <Tooltip formatter={(val) => `₹${val}`} contentStyle={styles.tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
                  <Bar dataKey="credit" name="Earnings" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="debit" name="Withdrawals" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 2. Pie Chart: System-wide Volume Distribution */}
            {summary && (
              <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Volume Distribution</div>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => `₹${val}`} contentStyle={styles.tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: "12px" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 3. Line Chart: Net Cumulative Balance Trajectory */}
            <div style={styles.chartCard} className="full-width-chart">
              <div style={styles.chartTitle}>System-wide Net Balance Trajectory</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" tickLine={false} />
                  <YAxis fontSize={11} stroke="#94a3b8" tickLine={false} axisLine={false} />
                  <Tooltip formatter={(val) => `₹${val}`} contentStyle={styles.tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    name="Net Flow"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ r: 3, fill: "#3b82f6" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Transactions Table/Cards */}
        <div style={styles.table}>
          <div className="table-header-desktop" style={styles.tableHeaderRow}>
            <span style={{ ...styles.th, flex: 1.5 }}>Driver</span>
            <span style={{ ...styles.th, flex: 2.5 }}>Description & Route</span>
            <span style={{ ...styles.th, flex: 1 }}>Type</span>
            <span style={{ ...styles.th, flex: 1 }}>Amount</span>
            <span style={{ ...styles.th, flex: 1.5 }}>Date</span>
          </div>

          {transactions.length === 0 ? (
            <div style={styles.emptyRow}>No transactions found.</div>
          ) : (
            transactions.map((t) => (
              <div key={t._id} className="transaction-row" style={styles.tableRow}>
                <div className="tx-cell" style={{ flex: 1.5, wordBreak: "break-word" }}>
                  <span className="mobile-label">Driver</span>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{t.driver?.name || "Unknown"}</div>
                  <div style={styles.subText}>{t.driver?.phone || "N/A"}</div>
                </div>

                <div className="tx-cell" style={{ flex: 2.5, wordBreak: "break-word" }}>
                  <span className="mobile-label">Description & Route</span>
                  <div style={styles.descText}>
                    {t.description || (t.type === "CREDIT" ? "Earning" : "Withdrawal")}
                  </div>
                  {t.booking && (
                    <div style={styles.addressBox}>
                      <span style={styles.addressLabel}>Pickup:</span> {t.booking.pickupLocation || "N/A"}
                      <br />
                      <span style={styles.addressLabel}>Drop:</span> {t.booking.dropLocation || "N/A"}
                    </div>
                  )}
                </div>

                <div className="tx-cell" style={{ flex: 1 }}>
                  <span className="mobile-label">Type</span>
                  <span
                    style={{
                      ...styles.badge,
                      backgroundColor: t.type === "CREDIT" ? "#dcfce7" : "#fee2e2",
                      color: t.type === "CREDIT" ? "#166534" : "#991b1b",
                    }}
                  >
                    {t.type}
                  </span>
                </div>

                <div className="tx-cell" style={{ flex: 1 }}>
                  <span className="mobile-label">Amount</span>
                  <span style={styles.amountText}>₹{t.amount}</span>
                </div>

                <div className="tx-cell" style={{ flex: 1.5 }}>
                  <span className="mobile-label">Date</span>
                  <span style={{ color: "#64748b", fontSize: "13px" }}>
                    {new Date(t.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span style={styles.pageInfo}>
              Page {page} of {totalPages}
            </span>
            <button
              style={styles.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: { background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  innerWrapper: { maxWidth: "1200px", margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  title: { margin: 0, fontSize: "24px", color: "#1e293b", fontWeight: "700" },
  subtitle: { margin: "4px 0 0 0", color: "#64748b", fontSize: "14px" },
  select: { padding: "10px 15px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", fontSize: "14px" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", margin: "20px 0" },
  statCard: { background: "#fff", borderRadius: "12px", padding: "16px", display: "flex", flexDirection: "column", gap: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  statLabel: { fontSize: "11px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase" },
  statValue: { fontSize: "20px", fontWeight: "800", color: "#1e293b" },

  chartCard: { background: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  chartTitle: { fontSize: "14px", fontWeight: "700", color: "#334155", marginBottom: "16px" },
  tooltipStyle: { borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" },

  table: { background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  tableHeaderRow: { padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: "700", fontSize: "12px", color: "#64748b", textTransform: "uppercase" },
  th: {},
  tableRow: { padding: "16px 20px", borderBottom: "1px solid #f1f5f9", fontSize: "14px", color: "#1e293b" },
  descText: { fontWeight: "600", color: "#0f172a" },
  addressBox: { marginTop: "6px", background: "#f8fafc", padding: "8px 10px", borderRadius: "6px", fontSize: "12px", color: "#475569", lineHeight: "1.5", wordBreak: "break-word" },
  addressLabel: { fontWeight: "600", color: "#334155" },
  subText: { fontSize: "12px", color: "#94a3b8", marginTop: "2px" },
  amountText: { fontWeight: "700", fontSize: "14px" },
  badge: { display: "inline-block", padding: "3px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "800" },
  emptyRow: { padding: "40px", textAlign: "center", color: "#94a3b8" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", marginTop: "20px" },
  pageBtn: { padding: "8px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontWeight: "600" },
  pageInfo: { fontSize: "14px", color: "#64748b", fontWeight: "600" },
  loading: { textAlign: "center", padding: "100px", fontSize: "18px", color: "#64748b" },
  error: { background: "#fef2f2", color: "#b91c1c", padding: "15px", borderRadius: "8px", marginBottom: "20px" },
};

export default AllWalletTransactions;